import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl, { Map as MapboxMap, Marker } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ".//Map.css";
import { BASE_URL } from "../../../data/constants";
import { getToken } from "../../../Services/authStore";
import OpenHoursEditor from "../OpenHoursEditor/OpenHoursEditor";
import { WEEKDAYS, getVenueCities, type OpenHours } from "../../../Services/Admin/Venues";

/**
 * Lägger till ett venue genom att peka på kartan.
 *
 * Flödet är: klicka fram en punkt, hämta förslag från Mapbox, välj ett och
 * få namn, adress och koordinater ifyllda. Resten skrivs för hand.
 *
 * Öppettiderna sattes tidigare som rå JSON i en textarea. Nu används samma
 * OpenHoursEditor som i venue-listan — servern kräver ändå öppettider, och
 * ett fält där man kan skriva syntaxfel är ett dåligt ställe att upptäcka
 * det på.
 */

mapboxgl.accessToken =
  "pk.eyJ1IjoibWFwLXBoaWwiLCJhIjoiY2xncDJhejUyMGJkdzNlbW1xYmp6OWhwcSJ9.PNkpEsE0hi_zfvGDY2af-Q";

type DbVenue = {
  id: string;
  name: string;
  address?: string | null;
  imageUrl?: string | null;
  lat?: number | null;
  lon?: number | null;
  timezone?: string | null;
  openHoursJson?: string | null;
};

type NearbyPlace = {
  id: string;
  name: string;
  address?: string;
  city?: string;
  category?: string;
  lon: number;
  lat: number;
};

type VenueDraft = {
  name: string;
  address: string;
  city: string;
  lat: string;
  lon: string;
  timezone: string;
  openHours: OpenHours;
  imageUrl: string;
};

/**
 * Utgångsläge för nya venues: kväll varje dag, natt mot helg.
 * Öppettider är obligatoriska, så redigeraren börjar ifylld i stället för
 * tom — det är ändå den vanligaste barveckan, och går att skriva över.
 */
function defaultOpenHours(): OpenHours {
  return {
    mon: [{ open: "17:00", close: "01:00" }],
    tue: [{ open: "17:00", close: "01:00" }],
    wed: [{ open: "17:00", close: "01:00" }],
    thu: [{ open: "17:00", close: "03:00" }],
    fri: [{ open: "17:00", close: "03:00" }],
    sat: [{ open: "12:00", close: "03:00" }],
    sun: [{ open: "12:00", close: "01:00" }],
  };
}

function round(n: number, p = 6) {
  const m = 10 ** p;
  return Math.round(n * m) / m;
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function safeParseFloat(s: string) {
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export default function AdminMap() {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);

  const venueMarkersRef = useRef<globalThis.Map<string, Marker>>(new globalThis.Map());
  const clickMarkerRef = useRef<Marker | null>(null);

  const [venues, setVenues] = useState<DbVenue[]>([]);
  const [selectedPoint, setSelectedPoint] = useState<{ lon: number; lat: number } | null>(null);

  const [nearby, setNearby] = useState<NearbyPlace[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyError, setNearbyError] = useState<string | null>(null);

  const [draft, setDraft] = useState<VenueDraft>({
    name: "",
    address: "",
    city: "",
    lat: "",
    lon: "",
    timezone: "Europe/Stockholm",
    openHours: defaultOpenHours(),
    imageUrl: "",
  });

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [knownCities, setKnownCities] = useState<string[]>([]);

  useEffect(() => {
    getVenueCities()
      .then((r) => setKnownCities(r.items.map((c) => c.city)))
      .catch(() => setKnownCities([]));
  }, []);

  const GBG: [number, number] = [11.97456, 57.70887];

  const reloadVenues = async () => {
    const r = await fetch(`${BASE_URL}/admin/venues?limit=200`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data?.error || "venues_fetch_failed");

    // Endpointen svarar { total, limit, offset, items }. Koden här läste
    // svaret som en naken array, så kartan visade alltid noll venues och
    // inga nålar alls.
    setVenues(Array.isArray(data) ? data : (data?.items ?? []));
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await reloadVenues();
      } catch (e: any) {
        console.error("admin venues fetch failed:", e?.message || e);
        if (alive) setVenues([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/map-phil/cmkvdu7w1005301sh1fi260ho",
      center: GBG,
      zoom: 12,
    });

    mapRef.current = map;

    // Kartan monteras när sektionen fälls ut, och mapbox hinner mäta
    // lådan innan den fått sin sluthöjd. Utan resize blir canvasen kvar
    // på fel höjd och lämnar ett grått fält längst ner.
    map.on("load", () => map.resize());

    map.on("click", (e) => {
      const lon = round(e.lngLat.lng, 6);
      const lat = round(e.lngLat.lat, 6);

      setSelectedPoint({ lon, lat });
      setNearby([]);
      setNearbyError(null);

      // click marker
      if (!clickMarkerRef.current) {
        clickMarkerRef.current = new mapboxgl.Marker({ color: "#111" })
          .setLngLat([lon, lat])
          .addTo(map);
      } else {
        clickMarkerRef.current.setLngLat([lon, lat]);
      }

      // autofill coords in draft
      setDraft((d) => ({
        ...d,
        lat: String(lat),
        lon: String(lon),
      }));
    });

    return () => {
      venueMarkersRef.current.forEach((m) => m.remove());
      venueMarkersRef.current.clear();

      clickMarkerRef.current?.remove();
      clickMarkerRef.current = null;

      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Render venue markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const valid = venues
      .filter((v) => Number.isFinite(v.lat ?? NaN) && Number.isFinite(v.lon ?? NaN))
      .map((v) => ({
        id: v.id,
        name: v.name,
        lat: v.lat as number,
        lon: v.lon as number,
      }));

    const nextIds = new Set(valid.map((v) => v.id));

    // remove missing
    const toDelete: string[] = [];
    venueMarkersRef.current.forEach((_m, id) => {
      if (!nextIds.has(id)) toDelete.push(id);
    });
    toDelete.forEach((id) => {
      venueMarkersRef.current.get(id)?.remove();
      venueMarkersRef.current.delete(id);
    });

    // add/update
    for (const v of valid) {
      const existing = venueMarkersRef.current.get(v.id);
      if (!existing) {
        const el = document.createElement("div");
        el.className = "admin-map-pin";
        el.title = v.name;

        el.addEventListener("click", (ev) => {
          ev.stopPropagation();

          map.flyTo({ center: [v.lon, v.lat], zoom: Math.max(map.getZoom(), 15), speed: 1.4 });

          const lon = round(v.lon, 6);
          const lat = round(v.lat, 6);
          setSelectedPoint({ lon, lat });

          if (!clickMarkerRef.current) {
            clickMarkerRef.current = new mapboxgl.Marker({ color: "#111" })
              .setLngLat([lon, lat])
              .addTo(map);
          } else {
            clickMarkerRef.current.setLngLat([lon, lat]);
          }

          // populate draft
          setDraft((d) => ({
            ...d,
            lat: String(lat),
            lon: String(lon),
          }));
        });

        const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
          .setLngLat([v.lon, v.lat])
          .addTo(map);

        venueMarkersRef.current.set(v.id, marker);
      } else {
        const ll = existing.getLngLat();
        if (ll.lng !== v.lon || ll.lat !== v.lat) existing.setLngLat([v.lon, v.lat]);
      }
    }
  }, [venues]);

  const searchNearby = async () => {
    if (!selectedPoint) return;

    setNearbyLoading(true);
    setNearbyError(null);
    setNearby([]);

    try {
      const { lon, lat } = selectedPoint;

      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/` +
        `${encodeURIComponent(`${lon},${lat}`)}.json` +
        `?access_token=${mapboxgl.accessToken}` +
        `&types=poi,address` +
        `&limit=10` +
        `&language=sv` +
        `&proximity=${lon},${lat}`;

      const r = await fetch(url);
      const j = await r.json();
      if (!r.ok) throw new Error("mapbox_failed");

      const items: NearbyPlace[] = (j?.features || [])
        .map((f: any) => {
          const name = f?.text || "Unknown";
          const address = f?.place_name || "";
          const cat = (f?.properties?.category || "").split(",")[0] || "";
          // place_name slutar på land, så staden tas ur kontexten i stället.
          const place = (f?.context || []).find((c: any) =>
            String(c?.id || "").startsWith("place.")
          );
          const [x, y] = f?.center || [null, null];

          return {
            id: f?.id || `${name}-${x}-${y}`,
            name,
            address,
            city: place?.text || undefined,
            category: cat,
            lon: Number(x),
            lat: Number(y),
          };
        })
        .filter((p: NearbyPlace) => Number.isFinite(p.lon) && Number.isFinite(p.lat));

      setNearby(items);
    } catch (e: any) {
      console.error("nearby search failed:", e?.message || e);
      setNearbyError(e?.message || "nearby_failed");
    } finally {
      setNearbyLoading(false);
    }
  };

  const applyNearbyToDraft = (p: NearbyPlace) => {
    const map = mapRef.current;
    if (map) {
      map.flyTo({ center: [p.lon, p.lat], zoom: Math.max(map.getZoom(), 16), speed: 1.4 });
      if (!clickMarkerRef.current) {
        clickMarkerRef.current = new mapboxgl.Marker({ color: "#111" }).setLngLat([p.lon, p.lat]).addTo(map);
      } else {
        clickMarkerRef.current.setLngLat([p.lon, p.lat]);
      }
    }

    setSelectedPoint({ lon: round(p.lon, 6), lat: round(p.lat, 6) });

    // Populate inputs
    setDraft((d) => ({
      ...d,
      name: p.name || d.name,
      address: p.address || d.address,
      city: p.city || d.city,
      lat: String(round(p.lat, 6)),
      lon: String(round(p.lon, 6)),
    }));
  };

  const coordText = useMemo(() => {
    if (!selectedPoint) return "";
    return `${selectedPoint.lat}, ${selectedPoint.lon}`;
  }, [selectedPoint]);

  const resetFields = () => {
    setDraft((d) => ({
      ...d,
      name: "",
      address: "",
      city: "",
      openHours: defaultOpenHours(),
      imageUrl: "",
    }));
    setSaveMsg(null);
  };

  const createVenue = async () => {
    setSaveMsg(null);

    const lat = safeParseFloat(draft.lat);
    const lon = safeParseFloat(draft.lon);

    if (!draft.name.trim()) return setSaveMsg("Name is required.");
    if (lat === null || lon === null) return setSaveMsg("Lat/Lon must be valid numbers.");

    // Obligatoriska: servern avvisar ett venue utan öppettider, och en
    // sensor på ett sådant ställe skulle aldrig känna igen något.
    const hasAnyHours = WEEKDAYS.some((day) => (draft.openHours[day] ?? []).length > 0);
    if (!hasAnyHours) return setSaveMsg("Opening hours are required.");

    setSaving(true);
    try {
      const r = await fetch(`${BASE_URL}/admin/create-venue`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          name: draft.name.trim(),
          address: draft.address.trim() || null,
          city: draft.city.trim() || null,
          lat,
          lon,
          timezone: draft.timezone.trim() || null,
          openHours: draft.openHours,
          imageUrl: draft.imageUrl.trim() || null,
        }),
      });

      const data = await r.json().catch(() => null);

      if (!r.ok) {
        throw new Error(data?.error || "create_failed");
      }

      setSaveMsg("✅ Venue created");
      resetFields();

      await reloadVenues();
    } catch (e: any) {
      console.error("create venue failed:", e);
      setSaveMsg(`${e?.message || "create_failed"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-map">
      <div className="admin-map-canvas">
        <div className="map-container" ref={mapContainer} />

        <div className="admin-map-hint">
          {selectedPoint ? coordText : "Click the map to pick a spot"}
          <span className="admin-map-count">{venues.length} venues</span>
        </div>
      </div>

      <div className="admin-map-form" onMouseDown={(e) => e.stopPropagation()}>
        <div className="admin-map-actions">
          <button
            type="button"
            disabled={!selectedPoint || nearbyLoading}
            onClick={searchNearby}
          >
            {nearbyLoading ? "Searching…" : "Find nearby"}
          </button>

          <button
            type="button"
            disabled={!selectedPoint}
            onClick={async () => {
              if (!selectedPoint) return;
              await copy(coordText);
            }}
          >
            Copy coords
          </button>

          {nearby.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setNearby([]);
                setNearbyError(null);
              }}
            >
              Clear suggestions
            </button>
          )}
        </div>

        {nearbyError && <div className="admin-error">{nearbyError}</div>}

        {nearby.length > 0 && (
          <div className="admin-map-suggestions">
            {nearby.map((p) => (
              <button
                key={p.id}
                type="button"
                className="admin-map-suggestion"
                onClick={() => applyNearbyToDraft(p)}
              >
                <strong>{p.name}</strong>
                {p.category && <em>{p.category}</em>}
                {p.address && <span>{p.address}</span>}
              </button>
            ))}
          </div>
        )}

        <label className="admin-field">
          <span className="admin-label">Name *</span>
          <input
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="Pustervik"
          />
        </label>

        <label className="admin-field">
          <span className="admin-label">Address</span>
          <input
            value={draft.address}
            onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
            placeholder="Järntorgsgatan 12"
          />
        </label>

        <label className="admin-field">
          <span className="admin-label">City</span>
          <input
            value={draft.city}
            onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
            placeholder="Göteborg"
            list="admin-map-cities"
          />
          <datalist id="admin-map-cities">
            {knownCities.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>

        <div className="admin-map-row">
          <label className="admin-field">
            <span className="admin-label">Lat *</span>
            <input
              value={draft.lat}
              onChange={(e) => setDraft((d) => ({ ...d, lat: e.target.value }))}
              placeholder="57.700408"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">Lon *</span>
            <input
              value={draft.lon}
              onChange={(e) => setDraft((d) => ({ ...d, lon: e.target.value }))}
              placeholder="11.954049"
            />
          </label>
        </div>

        <label className="admin-field">
          <span className="admin-label">Timezone</span>
          <input
            value={draft.timezone}
            onChange={(e) => setDraft((d) => ({ ...d, timezone: e.target.value }))}
            placeholder="Europe/Stockholm"
          />
        </label>

        <OpenHoursEditor
          value={draft.openHours}
          onChange={(openHours) => setDraft((d) => ({ ...d, openHours }))}
        />

        <label className="admin-field">
          <span className="admin-label">Image URL</span>
          <input
            value={draft.imageUrl}
            onChange={(e) => setDraft((d) => ({ ...d, imageUrl: e.target.value }))}
            placeholder="https://…"
          />
        </label>

        <div className="admin-map-actions">
          <button type="button" className="admin-btn--primary" disabled={saving} onClick={createVenue}>
            {saving ? "Creating…" : "Create venue"}
          </button>

          <button type="button" onClick={resetFields}>
            Reset fields
          </button>
        </div>

        {saveMsg && <p className="admin-hint">{saveMsg}</p>}
      </div>
    </div>
  );
}
